type Level = "info" | "warn" | "error"

// Server logs reach Datadog as raw stdout via the Vercel log drain. Emitting a
// single JSON line lets Datadog's JSON processor lift each context field
// (`durationMs`, `path`, …) into a queryable attribute instead of burying it in
// free text. `status` is Datadog's reserved level attribute, so it always wins
// over any caller-supplied key.
const serializeServerLine = (
  level: Level,
  message: string,
  context?: Record<string, unknown>,
): string => {
  try {
    return JSON.stringify({ message, ...context, status: level })
  } catch {
    // Non-serializable context (circular ref, BigInt) — keep the essentials.
    return JSON.stringify({ message, status: level })
  }
}

const makeLevel =
  (level: Level) =>
  (message: string, context?: Record<string, unknown>): void => {
    if (typeof window === "undefined") {
      // "info" maps to console.log; warn/error map to their own methods so the
      // stream-derived severity still matches.
      const fn = level === "info" ? console.log : console[level]
      fn(serializeServerLine(level, message, context))
      return
    }
    void import("@datadog/browser-logs").then(({ datadogLogs }) => {
      datadogLogs.logger[level](message, context)
    })
  }

export const logger = {
  info: makeLevel("info"),
  warn: makeLevel("warn"),
  error: makeLevel("error"),
}
